import { useState } from 'react';
import { X } from 'lucide-react';
import type { InsumoVista } from '@shared/types';
import { NumberField } from '@renderer/components/ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { useSessionStore } from '@renderer/store/useSessionStore';

/**
 * Sprint 133 — "Me equivoqué al ingresar las cantidades, ¿cómo puedo corregir?".
 *
 * Pregunta de la dueña su primer día en producción, sobre las cebollas: *"serían 8 piezas"*. Había
 * 1 en existencia, tecleó 8 en *Agregar stock* —que **suma**— y quedaron 9.
 *
 * `registrarAjuste` existía en el servidor desde hacía sprints, con su motivo obligatorio, su
 * validación de fracción y su movimiento con rastro. **No tenía ninguna puerta en la interfaz.**
 * Es el mismo defecto del Sprint 124 —función correcta que quien la necesita no puede alcanzar—,
 * y esta vez la única salida que le quedaba era registrar una **merma falsa**: mentirle al dato
 * que el negocio usa para costear, para arreglar un dedazo de captura.
 *
 * ## Por qué pide el número final y no cuánto sumar o restar
 *
 * Porque es como piensa quien cuenta: *"hay ocho"*. La pantalla de carga pregunta cuánto llegó
 * —y hace bien, es una entrada de mercancía— pero para corregir, pedir un delta obliga a hacer
 * una resta mental sobre un número que uno ya no cree. Aquí se teclea lo que hay, y el sistema
 * calcula el movimiento y lo deja anotado.
 */
export function ModalCorregirExistencia({
  insumo,
  onClose,
  onGuardado,
}: {
  insumo: InsumoVista | null;
  onClose: () => void;
  onGuardado: () => void;
}) {
  const [cantidadFinal, setCantidadFinal] = useState<number>(insumo?.stockActual ?? 0);
  const [motivo, setMotivo] = useState('');
  const sesion = useSessionStore((s) => s.sesion);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!insumo) return null;

  const actual = insumo.stockActual;
  const delta = Number((cantidadFinal - actual).toFixed(3));
  const hayCambio = Math.abs(delta) > 1e-6;

  /**
   * Motivos de verdad, para que el rastro sirva de algo.
   *
   * Un ajuste sin motivo es un número que nadie puede explicar tres semanas después, y el motivo
   * es lo que separa "me equivoqué tecleando" de "se echó a perder" — que para el dueño son cosas
   * distintas y una de ellas cuesta dinero.
   */
  const MOTIVOS = [
    'Me equivoqué al capturar',
    'Conteo físico: esto es lo que hay',
    'Se echó a perder',
    'Se cayó o se rompió',
  ];

  const guardar = async () => {
    setError(null);
    if (!hayCambio) {
      setError('Ese es el número que ya tenía. Cambia la cantidad o cierra la ventana.');
      return;
    }
    if (motivo.trim().length < 3) {
      setError('Dinos por qué se corrige: es lo que explica el movimiento después.');
      return;
    }
    if (!sesion) return;
    setGuardando(true);
    /*
      `usuarioId` viaja como en la merma, pero el servidor lo **pisa** con el de la sesión
      (`firmadoPorLaSesion`): quién ajustó el inventario no lo decide el navegador.
    */
    const res = await window.api.insumos.registrarAjuste({
      insumoId: insumo.id,
      delta,
      motivo: motivo.trim(),
      usuarioId: sesion.usuarioId,
    });
    setGuardando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    onGuardado();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-lg font-bold">Corregir existencia</h3>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <div className="text-xl font-bold text-slate-800">{insumo.nombre}</div>
            <div className="text-sm text-slate-600">
              Ahora dice que hay{' '}
              <span className="font-mono font-semibold">
                {actual} {insumo.unidad}
              </span>
            </div>
          </div>

          <div>
            <label className="label" htmlFor="cantFinal">
              ¿Cuánto hay de verdad? ({insumo.unidad})
            </label>
            <NumberField
              id="cantFinal"
              min={0}
              decimals={3}
              className="input py-3 text-right font-mono text-2xl"
              value={cantidadFinal}
              onChange={setCantidadFinal}
              autoFocus
            />
            <p className="help-text">
              Escribe lo que tienes, no la diferencia. El sistema hace la cuenta.
            </p>
          </div>

          {hayCambio && (
            <div
              className={`rounded-lg border p-3 text-sm ${
                delta > 0
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}
            >
              Se van a <b>{delta > 0 ? 'sumar' : 'quitar'}</b>{' '}
              <span className="font-mono font-bold">
                {Math.abs(delta)} {insumo.unidad}
              </span>
              , y queda anotado quién lo hizo y por qué.
            </div>
          )}

          <div>
            <label className="label" htmlFor="motivoAjuste">
              ¿Por qué?
            </label>
            <div className="mb-2 flex flex-wrap gap-2">
              {MOTIVOS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMotivo(m)}
                  className={`min-h-[36px] rounded-full border px-3 py-1 text-xs ${
                    motivo === m
                      ? 'border-primary bg-blue-50 font-semibold text-blue-900'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
            <input
              id="motivoAjuste"
              className="input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="O escríbelo con tus palabras"
              maxLength={200}
            />
          </div>

          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button onClick={onClose} className="btn-ghost" disabled={guardando}>
            Cancelar
          </button>
          <button onClick={guardar} className="btn-primary min-h-[44px]" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Corregir'}
          </button>
        </div>
      </div>
    </div>
  );
}
